import { RSAA } from 'redux-api-middleware'
import {
  getApiPath,
  makeQueryParams,
  webStorage,
} from 'sidecar-utility-library'
import { makePaginationParams } from 'sidecar-utilities'

import { types, initialState } from 'store/Bundles'
import { fetchDoctorDetail } from 'store/Doctor'
import { logError } from 'utils/monitoring/datadog'

export const fetchParentBundles = () => ({
  [RSAA]: {
    endpoint: getApiPath('care', `bundles/parentBundles`),
    method: 'GET',
    types: [
      types.FETCH_PARENT_BUNDLES_REQUEST,
      types.FETCH_PARENT_BUNDLES_SUCCESS,
      types.FETCH_PARENT_BUNDLES_ERROR,
    ],
  },
})

export const fetchParentBundleQuestions = bundleId => ({
  [RSAA]: {
    endpoint: getApiPath('care', `bundles/parentBundles/${bundleId}`),
    method: 'GET',
    types: [
      types.FETCH_PARENT_BUNDLES_QUESTIONS_REQUEST,
      types.FETCH_PARENT_BUNDLES_QUESTIONS_SUCCESS,
      types.FETCH_PARENT_BUNDLES_QUESTIONS_ERROR,
    ],
  },
})

export const fetchParentBundleQuestionsBySpecialty = specialties => ({
  [RSAA]: {
    endpoint: getApiPath(
      'care',
      `bundles/parentBundles/questions${makeQueryParams({
        specialties,
      })}`,
    ),
    method: 'GET',
    types: [
      types.FETCH_PARENT_BUNDLES_QUESTIONS_REQUEST,
      types.FETCH_PARENT_BUNDLES_QUESTIONS_SUCCESS,
      types.FETCH_PARENT_BUNDLES_QUESTIONS_ERROR,
    ],
  },
})
export const fetchBundleDetails = ({
  bundleId,
  zipCode,
  insuranceFilingUuid,
  bundleParams = {},
}) => ({
  [RSAA]: {
    endpoint: getApiPath(
      'care',
      `bundles/${bundleId}/bundle${makeQueryParams({
        insuranceFilingUuid,
        zipCode,
        ...bundleParams,
      })}`,
    ),
    method: 'GET',
    types: [
      types.FETCH_BUNDLE_DETAILS_REQUEST,
      {
        type: types.FETCH_BUNDLE_DETAILS_SUCCESS,
        meta: { requestedParentBundleId: bundleId }, // Track which parentBundleId we requested
      },
      {
        type: types.FETCH_BUNDLE_DETAILS_ERROR,
        meta: { requestedParentBundleId: bundleId },
      },
    ],
  },
})

export const fetchPlanId = ({ insuranceFilingUuid, policyDetailsPlanId }) => {
  return {
    [RSAA]: {
      headers: {
        'x-api-key': process.env.REACT_APP_X_API_KEY,
      },
      endpoint: () => {
        return getApiPath(
          'product',
          `pub/plans${makeQueryParams({
            planId: policyDetailsPlanId,
            stateFilingUuid: insuranceFilingUuid,
          })}`,
        )
      },
      method: 'GET',
      types: [
        types.FETCH_PLAN_ID_REQUEST,
        {
          type: types.FETCH_PLAN_ID_SUCCESS,
          meta: { insuranceFilingUuid, policyDetailsPlanId },
        },
        types.FETCH_PLAN_ID_ERROR,
      ],
    },
  }
}

export const fetchBundleInfoWithEstimate = params => ({
  [RSAA]: {
    endpoint: () => {
      const memberUuid = webStorage.getItem('sub')
      return getApiPath(
        'doc',
        `bundle/info-with-estimate${makeQueryParams({
          ...params,
          memberUuid,
          sessionId: window?.heap?.getSessionId?.() || '',
        })}`,
        'v2',
      )
    },
    method: 'GET',
    types: [
      types.FETCH_BUNDLE_INFO_WITH_ESTIMATE_REQUEST,
      types.FETCH_BUNDLE_INFO_WITH_ESTIMATE_SUCCESS,
      types.FETCH_BUNDLE_INFO_WITH_ESTIMATE_ERROR,
    ],
  },
})

export const fetchBundleInfoWithSiblingEstimates = params => ({
  [RSAA]: {
    endpoint: () => {
      const memberUuid = webStorage.getItem('sub')
      return getApiPath(
        'doc',
        `bundle/info-with-sibling-estimates${makeQueryParams({
          ...params,
          memberUuid,
          sessionId: window?.heap?.getSessionId?.() || '',
        })}`,
        'v2',
      )
    },
    method: 'GET',
    types: [
      types.FETCH_BUNDLE_INFO_WITH_SIBLING_ESTIMATES_REQUEST,
      types.FETCH_BUNDLE_INFO_WITH_SIBLING_ESTIMATES_SUCCESS,
      types.FETCH_BUNDLE_INFO_WITH_SIBLING_ESTIMATES_ERROR,
    ],
  },
})

/**
 * Search for doctors with optional pagination
 *
 * @param {Object} searchParams - Search parameters (query, lat, lon, zip, etc.)
 * @param {boolean} isPagination - Whether this is a pagination request (load
 *   more).
 *
 *   - False (default): New search, starts at page 0, replaces existing results
 *   - True: Load more, continues pagination, appends to existing results
 *
 * @returns {Object} RSAA action for doctor search
 */
export const searchForDoctors = (searchParams = {}, isPagination = false) => ({
  [RSAA]: {
    endpoint: ({ Bundles: { doctors } }) => {
      // FALC-1518: Only use pagination params when explicitly loading more results
      // For new searches, always start from page 0
      const paginationParams = isPagination ? makePaginationParams(doctors) : {}

      return getApiPath(
        'doc',
        `bundle/doctors${makeQueryParams({
          ...searchParams,
          ...paginationParams,
          sessionId: window?.heap?.getSessionId?.() || '',
        })}`,
        'v2',
      )
    },
    method: 'GET',
    types: [
      types.SEARCH_FOR_DOCTORS_REQUEST,
      types.SEARCH_FOR_DOCTORS_SUCCESS,
      types.SEARCH_FOR_DOCTORS_ERROR,
    ],
  },
})

export const lockBundle = ({
  bundleCode,
  locationUuid,
  locationName,
  providerNpi,
  providerRate,
  benefitAmountUsed,
}) => ({
  [RSAA]: {
    endpoint: getApiPath('care', 'bundle-locks/lock'),
    method: 'POST',
    body: JSON.stringify({
      memberUuid: webStorage.getItem('sub'),
      accountUuid: webStorage.getItem('aud'),
      // adminUuid,
      // lockedAt,
      bundleCode,
      locationUuid,
      locationName,
      providerNpi,
      providerRate,
      benefitAmountUsed,
    }),
    types: [
      types.LOCK_BUNDLE_REQUEST,
      types.LOCK_BUNDLE_SUCCESS,
      types.LOCK_BUNDLE_ERROR,
    ],
  },
})

export const clearDoctors = () => {
  return { type: types.CLEAR_DOCTORS }
}

export const clearBundleDetails = () => {
  return { type: types.CLEAR_BUNDLE_DETAILS }
}

export const clearBundleInfoWithEstimate = () => {
  return { type: types.CLEAR_BUNDLE_INFO_WITH_ESTIMATE }
}

export const clearBundleInfoWithSiblingEstimates = () => {
  return { type: types.CLEAR_BUNDLE_INFO_WITH_SIBLING_ESTIMATES }
}

export const clearParentBundleQuestions = () => {
  return { type: types.CLEAR_PARENT_BUNDLE_QUESTIONS }
}

/**
 * Deduplicates specialty entries from the specialties array by using a
 * combination of parentBundleId and displayName as a unique key
 */
const deduplicateSpecialties = specialties => {
  const uniqueSpecialtiesMap = new Map()

  // Use parentBundleId + displayName as a key to identify duplicates
  specialties.forEach(spec => {
    const key = `${spec.parentBundleId}|${spec.displayName.toLowerCase()}`
    if (!uniqueSpecialtiesMap.has(key)) {
      uniqueSpecialtiesMap.set(key, spec)
    }
  })

  return Array.from(uniqueSpecialtiesMap.values())
}

/**
 * Formats provider taxonomies into comma-separated string Filters taxonomies
 * based on addedSpecialtyFlag
 */
const formatProviderTaxonomiesForBundle = taxonomies => {
  if (!taxonomies || !Array.isArray(taxonomies)) return ''

  // Filter taxonomies - include if addedSpecialtyFlag is not explicitly false
  const filteredTaxonomies = taxonomies.filter(
    taxonomy => taxonomy?.addedSpecialtyFlag !== false,
  )

  return filteredTaxonomies.map(taxonomy => taxonomy.specialty).join(', ')
}

/**
 * Fetches provider specialties with bundle information for smart navigation
 * logic. Used to determine where to navigate the user when they click a
 * provider from the search results.
 *
 * This is a thunk action that:
 *
 * 1. Fetches provider details to get taxonomies
 * 2. Fetches questions by specialty
 * 3. Deduplicates specialties
 * 4. Returns specialties with questions array
 *
 * @param {Object} params - Parameters for fetching provider specialties
 * @param {string} params.npi - The provider's NPI number
 * @param {string} params.insuranceFilingUuid - The insurance filing UUID for
 *   bundle details
 * @returns {Function} Thunk that dispatches actions and returns specialty data
 */
export const fetchProviderSpecialtiesWithBundles =
  ({ npi, insuranceFilingUuid }) =>
  async dispatch => {
    try {
      // Fetch provider details to get taxonomies
      const doctorResponse = await dispatch(
        fetchDoctorDetail({
          npi,
          excludeSyntheticNpis: false,
          includeSpecialtiesAtLocation: true,
        }),
      )

      // Check if doctor fetch was successful
      if (
        !doctorResponse ||
        doctorResponse.error ||
        !doctorResponse.payload ||
        !doctorResponse.payload.taxonomies
      ) {
        return { specialties: [], taxonomies: '' }
      }

      const doctorData = doctorResponse.payload

      // Get taxonomies from doctor data, formatting for API
      const taxonomies = formatProviderTaxonomiesForBundle(
        doctorData.taxonomies,
      )

      if (!taxonomies) {
        return { specialties: [], taxonomies: '' }
      }

      // Fetch specialties based on taxonomies
      const questionsResponse = await dispatch(
        fetchParentBundleQuestionsBySpecialty(taxonomies),
      )

      // Check if questions fetch was successful
      if (
        !questionsResponse ||
        questionsResponse.error ||
        !questionsResponse.payload
      ) {
        return { specialties: [], taxonomies }
      }

      const questionsData = Array.isArray(questionsResponse.payload)
        ? questionsResponse.payload
        : []

      // Transform questionsData and deduplicate
      const deduplicatedSpecialties = deduplicateSpecialties(
        questionsData.map(q => ({
          parentBundleId: q.parentBundleId,
          displayName: q.displayName,
        })),
      )

      // Map back to include questions array
      const specialtiesWithQuestions = deduplicatedSpecialties.map(
        dedupedSpec => {
          const original = questionsData.find(
            q =>
              q.parentBundleId === dedupedSpec.parentBundleId &&
              q.displayName === dedupedSpec.displayName,
          )
          return {
            ...dedupedSpec,
            questions: original?.options?.questions || [],
          }
        },
      )

      return {
        specialties: specialtiesWithQuestions,
        taxonomies,
      }
    } catch (error) {
      logError('Error in fetchProviderSpecialtiesWithBundles:', {
        error,
        context: 'bundle-data-fetch',
        npi,
        insuranceFilingUuid,
      })
      return { specialties: [], taxonomies: '' }
    }
  }

let BUNDLES_REDUCER_LOOKUP = null
const getBundlesReducerLookup = () => {
  if (!BUNDLES_REDUCER_LOOKUP) {
    BUNDLES_REDUCER_LOOKUP = {
      [types.FETCH_PARENT_BUNDLES_REQUEST]: state => ({
        ...state,
        parentBundles: { ...state.parentBundles, busy: true },
      }),
      [types.FETCH_PARENT_BUNDLES_QUESTIONS_REQUEST]: state => ({
        ...state,
        parentBundleQuestions: { ...state.parentBundleQuestions, busy: true },
      }),
      [types.FETCH_BUNDLE_DETAILS_REQUEST]: state => ({
        ...state,
        bundleDetails: { ...state.bundleDetails, busy: true, error: '' },
      }),
      [types.FETCH_BUNDLE_INFO_WITH_ESTIMATE_REQUEST]: state => ({
        ...state,
        bundleInfoWithEstimate: { ...state.bundleInfoWithEstimate, busy: true },
      }),
      [types.FETCH_BUNDLE_INFO_WITH_SIBLING_ESTIMATES_REQUEST]: state => ({
        ...state,
        bundleInfoWithSiblingEstimates: {
          ...state.bundleInfoWithSiblingEstimates,
          busy: true,
        },
      }),
      [types.SEARCH_FOR_DOCTORS_REQUEST]: state => ({
        ...state,
        doctors: { ...state.doctors, busy: true },
      }),
      [types.FETCH_PARENT_BUNDLES_SUCCESS]: (state, payload) => ({
        ...state,
        parentBundles: {
          ...state.parentBundles,
          busy: false,
          entities: payload,
        },
      }),
      [types.FETCH_PARENT_BUNDLES_QUESTIONS_SUCCESS]: (state, payload) => ({
        ...state,
        parentBundleQuestions: {
          ...state.parentBundleQuestions,
          busy: false,
          entity: payload,
        },
      }),
      [types.FETCH_BUNDLE_DETAILS_SUCCESS]: (state, payload, meta) => ({
        ...state,
        bundleDetails: {
          ...state.bundleDetails,
          busy: false,
          entity: payload,
          requestedParentBundleId: meta?.requestedParentBundleId, // Store which parentBundleId we fetched for
        },
      }),
      [types.FETCH_BUNDLE_INFO_WITH_ESTIMATE_SUCCESS]: (state, payload) => ({
        ...state,
        bundleInfoWithEstimate: {
          ...state.bundleInfoWithEstimate,
          busy: false,
          entity: payload,
        },
      }),
      [types.FETCH_BUNDLE_INFO_WITH_SIBLING_ESTIMATES_SUCCESS]: (
        state,
        payload,
      ) => ({
        ...state,
        bundleInfoWithSiblingEstimates: {
          ...state.bundleInfoWithSiblingEstimates,
          busy: false,
          entity: payload,
        },
      }),
      [types.SEARCH_FOR_DOCTORS_SUCCESS]: (state, payload) => {
        const { page, size, totalPages, last, content = [] } = payload
        const isLastPageReached =
          page === totalPages - 1 || content?.length === 0 || Boolean(last)

        // Fix stale results persisting across searches
        // Page 0 = new search, replace entities
        // Page > 0 = pagination (load more), append to entities
        const entities =
          page === 0
            ? content
            : [...(state?.doctors?.entities || []), ...content]

        return {
          ...state,
          doctors: {
            ...state.doctors,
            busy: false,
            page: page,
            size: size,
            isLastPageReached,
            entities,
            error: '',
          },
        }
      },
      [types.FETCH_PARENT_BUNDLES_ERROR]: (state, payload) => ({
        ...state,
        parentBundles: {
          ...state.parentBundles,
          busy: false,
          error: payload?.response?.message,
        },
      }),
      [types.FETCH_PARENT_BUNDLES_QUESTIONS_ERROR]: (state, payload) => ({
        ...state,
        parentBundleQuestions: {
          ...state.parentBundleQuestions,
          busy: false,
          error: payload?.response?.message,
        },
      }),
      [types.FETCH_BUNDLE_DETAILS_ERROR]: (state, payload, meta) => ({
        ...state,
        bundleDetails: {
          ...state.bundleDetails,
          busy: false,
          error: payload?.response?.message,
          requestedParentBundleId: meta?.requestedParentBundleId,
        },
      }),
      [types.FETCH_BUNDLE_INFO_WITH_ESTIMATE_ERROR]: (state, payload) => ({
        ...state,
        bundleInfoWithEstimate: {
          ...state.bundleInfoWithEstimate,
          busy: false,
          error: payload?.response?.message,
        },
      }),
      [types.FETCH_BUNDLE_INFO_WITH_SIBLING_ESTIMATES_ERROR]: (
        state,
        payload,
      ) => ({
        ...state,
        bundleInfoWithSiblingEstimates: {
          ...state.bundleInfoWithSiblingEstimates,
          busy: false,
          error: payload?.response?.message,
        },
      }),
      [types.FETCH_PLAN_ID_REQUEST]: state => ({
        ...state,
        planId: { ...state.planId, busy: true },
      }),
      [types.FETCH_PLAN_ID_SUCCESS]: (state, payload, meta) => {
        const { content } = payload
        const { policyDetailsPlanId } = meta
        return {
          ...state,
          planId: {
            ...state.planId,
            busy: false,
            entity:
              content?.find(plan => plan.planId === policyDetailsPlanId) ||
              null,
          },
        }
      },
      [types.FETCH_PLAN_ID_ERROR]: (state, payload) => ({
        ...state,
        planId: {
          ...state.planId,
          busy: false,
          error: payload?.response?.message,
        },
      }),
      [types.LOCK_BUNDLE_REQUEST]: state => ({
        ...state,
        lock: { ...state.lock, busy: true },
      }),
      [types.LOCK_BUNDLE_SUCCESS]: (state, payload) => ({
        ...state,
        lock: { ...state.lock, busy: false, entity: payload },
      }),
      [types.LOCK_BUNDLE_ERROR]: (state, payload) => ({
        ...state,
        lock: {
          ...state.lock,
          busy: false,
          error: payload?.response?.message,
        },
      }),
      [types.SEARCH_FOR_DOCTORS_ERROR]: (state, payload) => ({
        ...state,
        doctors: {
          ...state.doctors,
          busy: false,
          error: payload?.response?.message,
        },
      }),
      [types.CLEAR_DOCTORS]: state => ({
        ...state,
        doctors: { ...initialState?.doctors },
      }),
      [types.CLEAR_BUNDLE_DETAILS]: state => ({
        ...state,
        bundleDetails: { ...initialState?.bundleDetails },
      }),
      [types.CLEAR_BUNDLE_INFO_WITH_ESTIMATE]: state => ({
        ...state,
        bundleInfoWithEstimate: { ...initialState?.bundleInfoWithEstimate },
      }),
      [types.CLEAR_BUNDLE_INFO_WITH_SIBLING_ESTIMATES]: state => ({
        ...state,
        bundleInfoWithSiblingEstimates: {
          ...initialState?.bundleInfoWithSiblingEstimates,
        },
      }),
      [types.CLEAR_PARENT_BUNDLE_QUESTIONS]: state => ({
        ...state,
        parentBundleQuestions: { ...initialState?.parentBundleQuestions },
      }),
    }
  }
  return BUNDLES_REDUCER_LOOKUP
}

export default (state = initialState, { type, payload = {}, meta = {} }) =>
  getBundlesReducerLookup()[type]?.(state, payload, meta) ?? state
